import { useEffect, useState } from 'react'

import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { IconFilterOutline, IconSortAsc } from 'assets/svg/sprite'
import { Modal } from 'components-ref/molecules/modal/modal'
import { AnalyticsContentSortModal } from 'components-ref/organisms/modals/analytics/content'
import {
    ContentFilterModal,
    FeedAnalytics,
    MessagesAnalytics,
    PremiumAnalytics,
    StoriesAnalytics
} from 'components-ref/templates/analytics'
import { useHeaderLayout } from 'context/headerLayoutContext'
import CategoriesDropdown from 'features/CategoriesDropdown/CategoriesDropdown'
import WithHeaderSection from 'layouts/WithHeaderSection/WithHeaderSection'
import { useUserProfile } from 'store'
import colors from 'styles/colors'
import { SortDirection } from 'types/enums'
import {
    AnalyticsContentModalSortBy,
    type ContentModalSortFilter
} from 'types/interfaces/IContentModalSortFilter'

type ContentTabs = 'feed' | 'messages' | 'premium' | 'stories'
type Filters = 'all_media' | 'videos' | 'photos'

export const Content = () => {
    const [activeTab, setActiveTab] = useState<ContentTabs>('feed')
    const [filter, setFilter] = useState<Filters>('all_media')
    const [sort, setSort] = useState<ContentModalSortFilter>({
        sort_by: AnalyticsContentModalSortBy.date_published,
        sort_value: SortDirection.DESCENDING
    })
    const userProfile = useUserProfile()
    const username = userProfile?.username ?? ''
    const { t } = useTranslation()
    
    const [activeTabLoaded, setActiveTabLoaded] = useState(false)

    const [filterModal, setFilterModal] = useState(false)
    const [sortModal, setSortModal] = useState(false)

    const [selectedCategory, setSelectedCategory] = useState<string>('')

    const selectCategory = (value: string) => {
    setSelectedCategory(value === 'all' ? '' : value)
    }

    const tabList: ContentTabs[] = ['feed', 'premium', 'messages', 'stories']
    const filterList: Filters[] = ['all_media', 'photos', 'videos']

    const normalizedCategoryId =
    selectedCategory || undefined

/*     useEffect(() => {
    setActiveTabLoaded(false)
    }, [activeTab, filter, sort, normalizedCategoryId]) */

    useEffect(() => {
    let lastTop = window.scrollY
    let ticking = false
    let hasUserScrolled = false

    // Hide immediately on first downward movement
    const SHOW_AT_TOP_PX = 0
    const DELTA_PX = 1

    const setHeaderNavHidden = (hidden: boolean) => {
        document.body.classList.toggle('header-nav-hidden', hidden)
    }

  const enableScrollAnimation = () => {
    if (!document.body.classList.contains('header-scroll-animating')) {
      document.body.classList.add('header-scroll-animating')
    }
  }

  const resetHeaderNav = () => {
    document.body.classList.remove('header-nav-hidden')
    document.body.classList.remove('header-scroll-animating')
    document.body.classList.remove('header-nav-ready')
  }

  // Reset on mount
  resetHeaderNav()

  // Enable transitions after first paint only
  requestAnimationFrame(() => {
    document.body.classList.add('header-nav-ready')
  })

  const onScroll = () => {
    hasUserScrolled = true

    const top = window.scrollY
    const delta = top - lastTop

    if (!ticking) {
      ticking = true

      requestAnimationFrame(() => {
        ticking = false

        if (!hasUserScrolled) return

        enableScrollAnimation()

        // Fully visible only at exact top
        if (top <= SHOW_AT_TOP_PX) {
          setHeaderNavHidden(false)
          lastTop = top
          return
        }

        // Ignore micro jitter
        if (Math.abs(delta) < DELTA_PX) {
          lastTop = top
          return
        }

        // Hide immediately when scrolling down
        // Show immediately when scrolling up
        setHeaderNavHidden(delta > 0)

        lastTop = top
      })
    }
  }

  window.addEventListener('scroll', onScroll, { passive: true })

  return () => {
    window.removeEventListener('scroll', onScroll)
    resetHeaderNav()
  }
}, [])
    return (
        <div className='content-container'>
        <WithHeaderSection
            customClass='analytics-content-whs'
            headerSection={
                <>
                    <div className="analytics-dropdown-container">
                        <div style={{ width: '100%', marginRight: '5px' }}>
                        <CategoriesDropdown
                            username={username}
                            selectedCategoryId={selectedCategory}
                            onSelectCategory={setSelectedCategory}
                        />
                        </div>
                        <ButtonWrapper>
                            <IconButton onClick={() => setSortModal(true)}>
                                <IconSortAsc />
                            </IconButton>
                            {sortModal && (
                                <AnalyticsContentSortModal
                                    onClose={() => setSortModal(false)}
                                    sort={sort}
                                    setSort={setSort}
                                    activeTab={activeTab}
                                />
                            )}
                            <IconButton onClick={() => setFilterModal(true)}>
                                <IconFilterOutline color="#ffffff" width="20px" height="20px" />
                            </IconButton>
                            {filterModal && (
                                <Modal
                                    title={t('filterBy')}
                                    isLayerTransparent={false}
                                    onClose={() => setFilterModal(false)}
                                    hasShadow
                                    maxWidth={40}
                                >
                                    <ContentFilterModal
                                        filterList={filterList}
                                        setActiveFilter={setFilter}
                                        closeFn={() => setFilterModal(false)}
                                    />
                                </Modal>
                            )}
                        </ButtonWrapper>
                    </div>

                    <div className="reports-links-container">
                        {tabList.map((tab) => (
                            <div
                                key={tab}
                                className={`reports-link ${activeTab === tab ? 'reports-link-active' : ''}`}
                                onClick={() => setActiveTab(tab)}
                            >
                                {t(tab)}
                            </div>
                        ))}
                    </div>
                </>
            }
        >
            {activeTab === 'feed' && (
                <FeedAnalytics selectedCategory={normalizedCategoryId} sort={sort} filter={filter} onLoaded={() => setActiveTabLoaded(true)} />
            )}
            {activeTab === 'premium' && (
                <PremiumAnalytics selectedCategory={normalizedCategoryId} sort={sort} filter={filter} />
            )}
            {activeTab === 'messages' && (
                <MessagesAnalytics selectedCategory={normalizedCategoryId} sort={sort} filter={filter} />
            )}
            {activeTab === 'stories' && (
                <StoriesAnalytics selectedCategory={normalizedCategoryId} sort={sort} filter={filter} />
            )}
        </WithHeaderSection>
        </div>
    )
}

const IconButton = styled.div`
    background-color: ${colors.slateBlue};
    border-radius: 1rem;
    display: flex;
    justify-content: center;
    align-items: center;
    height: 4.8rem;
    width: 4.8rem;
    margin-left: auto;
    cursor: pointer;
`

const ButtonWrapper = styled.div`
    display: flex;
    gap: 1rem;
`
